import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, TrendingUp, TrendingDown, Users, 
  RefreshCw, Plus, Edit2, Trash2, Eye, Search, Filter,
  BarChart3, Activity, DollarSign, Clock, CheckCircle,
  XCircle, ArrowUpRight, ArrowDownRight, Settings, Play
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const RISK_LEVELS = {
  BAJO: { color: '#22c55e', bg: '#dcfce7', label: 'Bajo' },
  MEDIO: { color: '#f59e0b', bg: '#fef3c7', label: 'Medio' },
  ALTO: { color: '#ef4444', bg: '#fee2e2', label: 'Alto' },
  CRITICO: { color: '#7f1d1d', bg: '#fecaca', label: 'Crítico' }
};

const FACTOR_CATEGORIES = {
  TRANSACCIONAL: { icon: DollarSign, color: '#3b82f6' },
  COMPORTAMIENTO: { icon: Activity, color: '#8b5cf6' },
  RELACIONAL: { icon: Users, color: '#10b981' },
  HISTORICO: { icon: Clock, color: '#f59e0b' },
  DOCUMENTAL: { icon: Shield, color: '#ef4444' }
};

const DEFAULT_FACTORS = [
  { id: 'volumen_atipico', nombre: 'Volumen Atípico', categoria: 'TRANSACCIONAL', peso: 15, descripcion: 'Volumen significativamente mayor al promedio histórico', umbral: 300 },
  { id: 'frecuencia_alta', nombre: 'Frecuencia Alta', categoria: 'TRANSACCIONAL', peso: 10, descripcion: 'Número de transacciones por encima del umbral', umbral: 50 },
  { id: 'montos_redondos', nombre: 'Montos Redondos', categoria: 'COMPORTAMIENTO', peso: 8, descripcion: 'Alto porcentaje de transacciones con montos redondos', umbral: 40 },
  { id: 'horario_inusual', nombre: 'Horario Inusual', categoria: 'COMPORTAMIENTO', peso: 7, descripcion: 'Actividad fuera del horario comercial', umbral: 30 },
  { id: 'contrapartes_riesgosas', nombre: 'Contrapartes Riesgosas', categoria: 'RELACIONAL', peso: 20, descripcion: 'Transacciones con entidades de alto riesgo', umbral: 1 },
  { id: 'nuevas_contrapartes', nombre: 'Nuevas Contrapartes', categoria: 'RELACIONAL', peso: 10, descripcion: 'Alto número de contrapartes nuevas en periodo corto', umbral: 10 },
  { id: 'alertas_previas', nombre: 'Alertas Previas', categoria: 'HISTORICO', peso: 12, descripcion: 'Historial de alertas en el sistema', umbral: 3 },
  { id: 'casos_asociados', nombre: 'Casos Asociados', categoria: 'HISTORICO', peso: 15, descripcion: 'Casos de investigación relacionados', umbral: 1 },
  { id: 'documentacion_incompleta', nombre: 'Documentación Incompleta', categoria: 'DOCUMENTAL', peso: 8, descripcion: 'Falta de documentos KYC', umbral: 50 },
  { id: 'tiempo_sin_verificar', nombre: 'Tiempo Sin Verificar', categoria: 'DOCUMENTAL', peso: 5, descripcion: 'Días desde última verificación KYC', umbral: 365 }
];

const RiskScoringClientes = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState([]);
  const [factors, setFactors] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [showFactorDialog, setShowFactorDialog] = useState(false);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingFactor, setEditingFactor] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    nivel: '',
    search: '',
    ordenar: 'score_desc'
  });

  // Form state for factor
  const [factorForm, setFactorForm] = useState({
    nombre: '',
    descripcion: '',
    categoria: 'TRANSACCIONAL',
    peso: 10,
    umbral: 100,
    activo: true
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [scoresRes, factorsRes, statsRes] = await Promise.all([
        api.get('/risk-scoring/clients', { params: { ...filters, limit: 100 } }),
        api.get('/risk-scoring/factors'),
        api.get('/risk-scoring/stats')
      ]);
      
      setScores(scoresRes.data.clients || []);
      setFactors(factorsRes.data.factors || DEFAULT_FACTORS);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Use mock data if API not available
      setScores(generateMockScores());
      setFactors(DEFAULT_FACTORS);
      setStats(generateMockStats());
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateMockScores = () => {
    const mockClients = [
      { cuit: '20-12345678-9', nombre: 'COMERCIAL ABC S.A.', score: 78, nivel: 'ALTO' },
      { cuit: '30-87654321-0', nombre: 'INVERSIONES XYZ S.R.L.', score: 45, nivel: 'MEDIO' },
      { cuit: '27-11223344-5', nombre: 'MARIA GARCIA', score: 23, nivel: 'BAJO' },
      { cuit: '20-99887766-1', nombre: 'JUAN PEREZ', score: 92, nivel: 'CRITICO' },
      { cuit: '30-55667788-2', nombre: 'SERVICIOS DELTA S.A.', score: 56, nivel: 'MEDIO' },
    ];
    return mockClients.map(c => ({
      ...c,
      factores: DEFAULT_FACTORS.slice(0, 5).map(f => ({
        ...f,
        valor: Math.random() * 100,
        contribucion: Math.floor(Math.random() * 20)
      })),
      tendencia: Math.random() > 0.5 ? 'UP' : 'DOWN',
      ultima_actualizacion: new Date().toISOString()
    }));
  };

  const generateMockStats = () => ({
    total_clientes: 1250,
    distribucion: [
      { nivel: 'BAJO', count: 850, porcentaje: 68 },
      { nivel: 'MEDIO', count: 280, porcentaje: 22.4 },
      { nivel: 'ALTO', count: 95, porcentaje: 7.6 },
      { nivel: 'CRITICO', count: 25, porcentaje: 2 }
    ],
    promedio_score: 34.5,
    tendencia_mensual: [
      { mes: 'Ene', promedio: 32 },
      { mes: 'Feb', promedio: 33 },
      { mes: 'Mar', promedio: 35 },
      { mes: 'Abr', promedio: 34.5 }
    ]
  });

  const handleRecalculate = async () => {
    toast.info('Recalculando scores...');
    try {
      await api.post('/risk-scoring/recalculate');
      toast.success('Scores recalculados exitosamente');
      loadData();
    } catch (error) {
      toast.error('Error al recalcular');
    }
  };

  const handleSaveFactor = async () => {
    try {
      if (editingFactor) {
        await api.put(`/risk-scoring/factors/${editingFactor.id}`, factorForm);
        toast.success('Factor actualizado');
      } else {
        await api.post('/risk-scoring/factors', factorForm);
        toast.success('Factor creado');
      }
      setShowFactorDialog(false);
      resetFactorForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar factor');
    }
  };

  const handleDeleteFactor = async (factorId) => {
    if (!window.confirm('¿Eliminar este factor de riesgo?')) return;
    try {
      await api.delete(`/risk-scoring/factors/${factorId}`);
      toast.success('Factor eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditFactor = (factor) => {
    setEditingFactor(factor);
    setFactorForm({
      nombre: factor.nombre,
      descripcion: factor.descripcion || '',
      categoria: factor.categoria,
      peso: factor.peso,
      umbral: factor.umbral,
      activo: factor.activo !== false
    });
    setShowFactorDialog(true);
  };

  const resetFactorForm = () => {
    setEditingFactor(null);
    setFactorForm({
      nombre: '',
      descripcion: '',
      categoria: 'TRANSACCIONAL',
      peso: 10,
      umbral: 100,
      activo: true
    });
  };

  const openClientDetail = (client) => {
    setSelectedClient(client);
    setShowClientDialog(true);
  };

  // Parte I: la etiqueta usa los 5 niveles del score único (nivel_detalle), igual que la ficha y el caso.
  const getRiskBadge = (nivel, detalle) => {
    const config = detalle === 'MUY BAJO' ? { color: '#475569', bg: '#e2e8f0' } : (RISK_LEVELS[nivel] || RISK_LEVELS.BAJO);
    const label = detalle ? detalle.charAt(0) + detalle.slice(1).toLowerCase() : config.label;
    return (
      <Badge style={{ backgroundColor: config.bg, color: config.color }}>
        {label}
      </Badge>
    );
  };

  const COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#7f1d1d'];

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="risk-scoring-page">
      {/* Header (pt-10: deja libre la barra fija de búsqueda y notificaciones de arriba a la derecha) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pt-10">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shield size={28} className="text-purple-600" />
            Scoring de Riesgo de Clientes
          </h1>
          <p className="text-slate-600 mt-1">Evaluación automática del nivel de riesgo de clientes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button onClick={handleRecalculate} style={{ backgroundColor: '#7c3aed', color: 'white' }}>
            <Play size={16} className="mr-2" />
            Recalcular Scores
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-white">
            <BarChart3 size={16} className="mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="clientes" className="data-[state=active]:bg-white">
            <Users size={16} className="mr-2" />
            Clientes
          </TabsTrigger>
          <TabsTrigger value="factores" className="data-[state=active]:bg-white">
            <Settings size={16} className="mr-2" />
            Factores de Riesgo
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Users className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total_clientes || 0}</p>
                  <p className="text-sm text-slate-500">Clientes Evaluados</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <BarChart3 className="text-purple-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats?.promedio_score?.toFixed(1) || 0}</p>
                  <p className="text-sm text-slate-500">Score Promedio</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <AlertTriangle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">
                    {stats?.distribucion?.find(d => d.nivel === 'CRITICO')?.count || 0}
                  </p>
                  <p className="text-sm text-slate-500">Clientes Críticos</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {stats?.distribucion?.find(d => d.nivel === 'BAJO')?.porcentaje?.toFixed(1) || 0}%
                  </p>
                  <p className="text-sm text-slate-500">Bajo Riesgo</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-2 gap-6">
            {/* Distribution Pie */}
            <Card className="p-4 bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Distribución por Nivel de Riesgo</h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats?.distribucion || []}
                      dataKey="count"
                      nameKey="nivel"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ nivel, porcentaje }) => `${nivel}: ${porcentaje?.toFixed(1)}%`}
                    >
                      {(stats?.distribucion || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Trend Line */}
            <Card className="p-4 bg-white">
              <h3 className="font-semibold text-slate-900 mb-4">Tendencia Score Promedio</h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats?.tendencia_mensual || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mes" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="promedio" 
                      stroke="#7c3aed" 
                      strokeWidth={2}
                      dot={{ fill: '#7c3aed' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Top Risk Clients */}
          <Card className="p-4 bg-white">
            <h3 className="font-semibold text-slate-900 mb-4">Clientes de Mayor Riesgo</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CUIT</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Tendencia</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scores.filter(s => s.nivel === 'CRITICO' || s.nivel === 'ALTO').slice(0, 5).map(client => (
                  <TableRow key={client.cuit}>
                    <TableCell className="font-medium">{client.nombre}</TableCell>
                    <TableCell className="font-mono text-sm">{client.cuit}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full transition-all"
                            style={{ 
                              width: `${client.score}%`,
                              backgroundColor: RISK_LEVELS[client.nivel]?.color 
                            }}
                          />
                        </div>
                        <span className="font-semibold">{client.score}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getRiskBadge(client.nivel, client.nivel_detalle)}</TableCell>
                    <TableCell>
                      {client.tendencia === 'UP' ? (
                        <ArrowUpRight className="text-red-500" size={18} />
                      ) : (
                        <ArrowDownRight className="text-green-500" size={18} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => openClientDetail(client)}>
                        <Eye size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Clientes Tab */}
        <TabsContent value="clientes" className="space-y-4">
          {/* Filters */}
          <Card className="p-4 bg-white">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Search size={18} className="text-slate-400" />
                <Input
                  placeholder="Buscar por nombre o CUIT..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="flex-1"
                />
              </div>
              <Select value={filters.nivel || "all"} onValueChange={(v) => setFilters({ ...filters, nivel: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Nivel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los niveles</SelectItem>
                  <SelectItem value="BAJO">Bajo</SelectItem>
                  <SelectItem value="MEDIO">Medio</SelectItem>
                  <SelectItem value="ALTO">Alto</SelectItem>
                  <SelectItem value="CRITICO">Crítico</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.ordenar} onValueChange={(v) => setFilters({ ...filters, ordenar: v })}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score_desc">Mayor Score</SelectItem>
                  <SelectItem value="score_asc">Menor Score</SelectItem>
                  <SelectItem value="nombre_asc">Nombre A-Z</SelectItem>
                  <SelectItem value="fecha_desc">Más reciente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Clients Table */}
          <Card className="bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CUIT</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Factores Principales</TableHead>
                  <TableHead>Última Actualización</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <RefreshCw className="animate-spin mx-auto text-slate-400" size={24} />
                    </TableCell>
                  </TableRow>
                ) : scores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No se encontraron clientes
                    </TableCell>
                  </TableRow>
                ) : (
                  scores.map(client => (
                    <TableRow key={client.cuit}>
                      <TableCell className="font-medium">{client.nombre}</TableCell>
                      <TableCell className="font-mono text-sm">{client.cuit}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full"
                              style={{ 
                                width: `${client.score}%`,
                                backgroundColor: RISK_LEVELS[client.nivel]?.color 
                              }}
                            />
                          </div>
                          <span className="font-semibold text-sm">{client.score}</span>
                        </div>
                      </TableCell>
                      <TableCell>{getRiskBadge(client.nivel, client.nivel_detalle)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(client.factores || []).slice(0, 3).map((f, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {f.nombre?.substring(0, 12)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {client.ultima_actualizacion ? new Date(client.ultima_actualizacion).toLocaleDateString('es-AR') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => openClientDetail(client)}>
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Factores Tab */}
        <TabsContent value="factores" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-slate-600">Configura los factores que determinan el scoring de riesgo</p>
            <Button onClick={() => { resetFactorForm(); setShowFactorDialog(true); }}>
              <Plus size={16} className="mr-2" />
              Nuevo Factor
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(FACTOR_CATEGORIES).map(([cat, catConfig]) => {
              const catFactors = factors.filter(f => f.categoria === cat);
              const Icon = catConfig.icon;
              return (
                <Card key={cat} className="p-4 bg-white">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon size={20} style={{ color: catConfig.color }} />
                    <h3 className="font-semibold text-slate-900">{cat}</h3>
                    <Badge variant="outline" className="ml-auto">{catFactors.length} factores</Badge>
                  </div>
                  <div className="space-y-3">
                    {catFactors.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Sin factores configurados</p>
                    ) : (
                      catFactors.map(factor => (
                        <div key={factor.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{factor.nombre}</span>
                              {factor.activo === false && (
                                <Badge variant="outline" className="text-xs bg-slate-100">Inactivo</Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{factor.descripcion}</p>
                            <div className="flex gap-3 mt-2 text-xs text-slate-600">
                              <span>Peso: <strong>{factor.peso}%</strong></span>
                              <span>Umbral: <strong>{factor.umbral}</strong></span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEditFactor(factor)}>
                              <Edit2 size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteFactor(factor.id)} className="text-red-500 hover:text-red-700">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Factor Dialog */}
      <Dialog open={showFactorDialog} onOpenChange={setShowFactorDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle>{editingFactor ? 'Editar Factor' : 'Nuevo Factor de Riesgo'}</DialogTitle>
            <DialogDescription>Configure los parámetros del factor de riesgo</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={factorForm.nombre}
                onChange={(e) => setFactorForm({ ...factorForm, nombre: e.target.value })}
                placeholder="Ej: Volumen Atípico"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={factorForm.descripcion}
                onChange={(e) => setFactorForm({ ...factorForm, descripcion: e.target.value })}
                placeholder="Descripción del factor..."
                className="mt-1"
              />
            </div>
            <div>
              <Label>Categoría</Label>
              <Select value={factorForm.categoria} onValueChange={(v) => setFactorForm({ ...factorForm, categoria: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(FACTOR_CATEGORIES).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Peso (% del score total)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={factorForm.peso}
                  onChange={(e) => setFactorForm({ ...factorForm, peso: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Umbral de Activación</Label>
                <Input
                  type="number"
                  min="0"
                  value={factorForm.umbral}
                  onChange={(e) => setFactorForm({ ...factorForm, umbral: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={factorForm.activo}
                onCheckedChange={(v) => setFactorForm({ ...factorForm, activo: v })}
              />
              <Label>Factor activo</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFactorDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveFactor} disabled={!factorForm.nombre}>
              {editingFactor ? 'Guardar Cambios' : 'Crear Factor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Detail Dialog */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          {selectedClient && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield size={20} style={{ color: RISK_LEVELS[selectedClient.nivel]?.color }} />
                  Detalle de Riesgo: {selectedClient.nombre}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Score Summary */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-500">CUIT: {selectedClient.cuit}</p>
                    <p className="text-2xl font-bold" style={{ color: RISK_LEVELS[selectedClient.nivel]?.color }}>
                      Score: {selectedClient.score}/100
                    </p>
                  </div>
                  <div className="text-right">
                    {getRiskBadge(selectedClient.nivel, selectedClient.nivel_detalle)}
                    <p className="text-xs text-slate-400 mt-2">
                      Última actualización: {selectedClient.ultima_actualizacion ? new Date(selectedClient.ultima_actualizacion).toLocaleDateString('es-AR') : '-'}
                    </p>
                  </div>
                </div>

                {/* Score Bar */}
                <div>
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>0</span>
                    <span>25</span>
                    <span>50</span>
                    <span>75</span>
                    <span>100</span>
                  </div>
                  <div className="h-4 bg-slate-200 rounded-full overflow-hidden relative">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${selectedClient.score}%`,
                        background: `linear-gradient(90deg, #22c55e 0%, #f59e0b 50%, #ef4444 100%)`
                      }}
                    />
                    <div 
                      className="absolute top-0 h-full w-1 bg-slate-900"
                      style={{ left: `${selectedClient.score}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-green-600">Bajo</span>
                    <span className="text-amber-600">Medio</span>
                    <span className="text-red-600">Alto</span>
                    <span className="text-red-800">Crítico</span>
                  </div>
                </div>

                {/* Factors Breakdown */}
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Factores de Riesgo Detectados</h4>
                  <div className="space-y-2">
                    {(selectedClient.factores || []).map((factor, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-2 h-8 rounded"
                            style={{ backgroundColor: FACTOR_CATEGORIES[factor.categoria]?.color || '#64748b' }}
                          />
                          <div>
                            <p className="font-medium text-sm">{factor.nombre}</p>
                            <p className="text-xs text-slate-500">{factor.categoria}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-slate-900">+{factor.contribucion}</p>
                          <p className="text-xs text-slate-500">puntos</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowClientDialog(false)}>Cerrar</Button>
                <Button onClick={() => {
                  setShowClientDialog(false);
                  // Navigate to client detail
                  window.location.href = `/m/clientes/cliente/${selectedClient.cuit}`;
                }}>
                  Ver Perfil Completo
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RiskScoringClientes;
